import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import apiService from '../services/apiService';
import { MiniCalendar, SectionHeader, StatCard, Modal, BookingBadge, EmptyState, Toggle } from '../components/UI';
import { LayoutDashboard, Calendar, Users, User, DollarSign, Plus, Trash2, Pencil, Video, Briefcase, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ExpertProfileEdit from './ExpertProfileEdit';

const TABS = [
  { id: 'overview',  label: 'Overview',           Icon: LayoutDashboard },
  { id: 'services',  label: 'Services & Pricing', Icon: Briefcase },
  { id: 'calendar',  label: 'Calendar & Slots',   Icon: Calendar },
  { id: 'bookings',  label: 'Bookings',           Icon: Users },
  { id: 'earnings',  label: 'Earnings',           Icon: DollarSign },
  { id: 'profile',   label: 'My Profile',         Icon: User },
  { id: 'logout',    label: 'Logout',             Icon: LogOut },
];

export default function ExpertDashboard() {
  const { user, slots, addSlot, deleteSlot, refreshSlots, bookings, acceptBooking, declineBooking, refreshBookings, categories, tags, services, addService, updateService, removeService, refreshServices, toast, logout, wallet, loadWallet, expertPacks, addExpertPack, updateExpertPack, removeExpertPack, refreshExpertPacks } = useApp();
  const expertServices = services.filter(s => String(s.expert_id) === String(user?.id));
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('expert_tab') || 'overview');
  const [calDate, setCalDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [bookingFilter, setBookingFilter] = useState('all');
  const [slotTypeFilter, setSlotTypeFilter] = useState('1:1'); // '1:1' | 'group'
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [showAddSvc, setShowAddSvc] = useState(false);
  const [showAddPack, setShowAddPack] = useState(false);
  const [slotForm, setSlotForm] = useState({ date: '', start: '10:00', end: '11:00', service: '', price: 1500, notes: '', type: '1:1', minJoinees: 2, maxJoinees: 10 });

  // Bulk Slot Scheduler State
  const [bulkSlotForm, setBulkSlotForm] = useState({
    startDate: '',
    timeSlots: [{ start: '10:00', end: '11:00' }],
    service: '',
    price: 1500,
    notes: '',
    type: '1:1',
    minJoinees: 2,
    maxJoinees: 10,
    recurrence: 'none', // 'none', 'weekly', 'daily'
    recurrenceDays: [], // For weekly
    endDate: '',
  });
  const [previewSlots, setPreviewSlots] = useState([]);

  const [svcForm, setSvcForm] = useState({ catId: '', title: '', desc: '', duration: '60 min', price: 1500, subcategories: [], tags: [] });
  const [packForm, setPackForm] = useState({ title: '', description: '', session_count: 5, price: 2500, expiry_days: 90, category_id: '', subcategories: [] });
  const [packType, setPackType] = useState('1:1'); // '1:1' or 'group'
  const [groupPackForm, setGroupPackForm] = useState({ title: '', description: '', session_count: 8, price: 4000, expiry_days: 90, category_id: '', subcategories: [], min_learners: 3, max_learners: 10, start_date: '', days_of_week: [] });
  const [editingSvc, setEditingSvc] = useState(null);
  const [editingPack, setEditingPack] = useState(null);
  const [showSubPickerPack, setShowSubPickerPack] = useState(false);
  const [showSubPickerGroupPack, setShowSubPickerGroupPack] = useState(false);
  const availableSubcatsPack = categories.find(c => c.id === packForm.category_id)?.subs || [];
  const availableSubcatsGroupPack = categories.find(c => c.id === groupPackForm.category_id)?.subs || [];
  const daysOfWeekOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [showSubPicker, setShowSubPicker] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [payoutForm, setPayoutForm] = useState({ amount: '', upi_id: '', bank_name: '', account_number: '', ifsc_code: '' });
  const [payoutMethod, setPayoutMethod] = useState('upi');
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [walletLoading, setWalletLoading] = useState(false);
  const [learnerRequirements, setLearnerRequirements] = useState([]);
  const [reqBrowseLoading, setReqBrowseLoading] = useState(false);
  const [slotCreationLoading, setSlotCreationLoading] = useState(false);

  const mySlots    = slots.filter(s => s.expertId === user?.id);
  const myBookings = bookings.filter(b => String(b.expertId) === String(user?.id));
  const pending    = myBookings.filter(b => b.status === 'pending');
  const confirmed  = myBookings.filter(b => b.status === 'confirmed');
  const completed  = myBookings.filter(b => b.status === 'completed');

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  // Refresh relevant data whenever the active tab changes
  useEffect(() => {
    switch (activeTab) {
      case 'overview':
        setWalletLoading(true);
        Promise.all([loadWallet(), refreshBookings(), refreshSlots()])
          .catch(err => console.error('Overview refresh error:', err))
          .finally(() => setWalletLoading(false));
        break;
      case 'calendar':
        refreshSlots();
        break;
      case 'bookings':
        refreshBookings();
        break;
      case 'services':
        refreshServices();
        refreshExpertPacks();
        setReqBrowseLoading(true);
        apiService.getAllRequirements()
          .then(res => { if (res.success) setLearnerRequirements(res.data); })
          .catch(() => {})
          .finally(() => setReqBrowseLoading(false));
        break;
      case 'earnings':
        setWalletLoading(true);
        Promise.all([loadWallet(), apiService.getPayouts()])
          .then(([, payoutsRes]) => { if (payoutsRes?.success) setPayouts(payoutsRes.data); })
          .catch(err => console.error('Earnings refresh error:', err))
          .finally(() => setWalletLoading(false));
        break;
      default:
        break;
    }
  }, [activeTab]);

  const filteredBookings = bookingFilter === 'all' ? myBookings : myBookings.filter(b => b.status === bookingFilter);

  const slotsForDate = (d) => {
    const ds = `${calDate.getFullYear()}-${String(calDate.getMonth()+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    return mySlots.filter(s => {
      const dateMatch = s.date === ds;
      const typeMatch = (slotTypeFilter === '1:1' && s.type !== 'group') || (slotTypeFilter === 'group' && s.type === 'group');
      return dateMatch && typeMatch;
    });
  };

  // Calculate preview slots based on recurrence
  const calculatePreviewSlots = (form) => {
    if (!form.startDate || form.timeSlots.length === 0) return [];

    const slots = [];
    const startDate = new Date(form.startDate);
    const endDate = form.endDate ? new Date(form.endDate) : new Date(startDate.getTime() + 90 * 24 * 60 * 60 * 1000); // Default 90 days

    if (form.recurrence === 'none') {
      // Single date
      form.timeSlots.forEach(ts => {
        slots.push({
          date: form.startDate,
          time: `${ts.start}–${ts.end}`,
        });
      });
    } else if (form.recurrence === 'weekly') {
      // Weekly recurring on selected days
      const dayIndexMap = { 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4, 'Friday': 5, 'Saturday': 6, 'Sunday': 0 };
      let currentDate = new Date(startDate);

      while (currentDate <= endDate) {
        const dayName = currentDate.toLocaleString('en-US', { weekday: 'long' });
        if (form.recurrenceDays.includes(dayName)) {
          form.timeSlots.forEach(ts => {
            const dateStr = currentDate.toISOString().split('T')[0];
            slots.push({
              date: dateStr,
              time: `${ts.start}–${ts.end}`,
            });
          });
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else if (form.recurrence === 'daily') {
      // Daily recurring
      let currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        form.timeSlots.forEach(ts => {
          const dateStr = currentDate.toISOString().split('T')[0];
          slots.push({
            date: dateStr,
            time: `${ts.start}–${ts.end}`,
          });
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }
    }

    return slots.slice(0, 30); // Limit preview to 30 slots
  };

  // Update preview when form changes
  useEffect(() => {
    setPreviewSlots(calculatePreviewSlots(bulkSlotForm));
  }, [bulkSlotForm]);

  const handleAddBulkSlots = async () => {
    if (!bulkSlotForm.startDate || bulkSlotForm.timeSlots.length === 0) {
      toast('Select start date and add time slots', 'error');
      return;
    }
    if (bulkSlotForm.recurrence === 'weekly' && bulkSlotForm.recurrenceDays.length === 0) {
      toast('Select at least one day for weekly recurrence', 'error');
      return;
    }
    if (bulkSlotForm.recurrence !== 'none' && !bulkSlotForm.endDate) {
      toast('Set end date for recurring slots', 'error');
      return;
    }

    setSlotCreationLoading(true);
    const service = bulkSlotForm.service || (expertServices[0]?.title || 'Session');
    const slotsToCreate = previewSlots.map(s => ({
      date: s.date,
      start: s.time.split('–')[0],
      end: s.time.split('–')[1],
      service,
      price: bulkSlotForm.price,
      notes: bulkSlotForm.notes,
      type: bulkSlotForm.type,
      minJoinees: bulkSlotForm.minJoinees,
      maxJoinees: bulkSlotForm.maxJoinees,
    }));

    try {
      let successCount = 0;
      for (const slot of slotsToCreate) {
        try {
          await addSlot(slot);
          successCount++;
        } catch (err) {
          console.error('Failed to add slot:', err);
        }
      }
      toast(`${successCount} slots added to calendar ✓`, 'success');
      setShowAddSlot(false);
      setBulkSlotForm({
        startDate: '',
        timeSlots: [{ start: '10:00', end: '11:00' }],
        service: '',
        price: 1500,
        notes: '',
        type: '1:1',
        minJoinees: 2,
        maxJoinees: 10,
        recurrence: 'none',
        recurrenceDays: [],
        endDate: '',
      });
    } catch (err) {
      toast(err.message || 'Failed to add slots', 'error');
    } finally {
      setSlotCreationLoading(false);
    }
  };

  const handleAddSlot = async () => {
    if (!slotForm.date || !slotForm.start || !slotForm.end) { toast('Fill all required fields', 'error'); return; }
    const service = slotForm.service || (expertServices[0]?.title || 'Session');
    setSlotCreationLoading(true);
    try {
      await addSlot({ ...slotForm, service });
      setShowAddSlot(false);
      setSlotForm({ date: '', start: '10:00', end: '11:00', service: '', price: 1500, notes: '', type: '1:1', minJoinees: 2, maxJoinees: 10 });
      toast('Slot added to calendar ✓', 'success');
    } catch (err) {
      toast(err.message || 'Failed to add slot', 'error');
    } finally {
      setSlotCreationLoading(false);
    }
  };

  const emptySvcForm = { catId: '', title: '', desc: '', duration: '60 min', price: 1500, subcategories: [], tags: [] };

  const handleOpenEditSvc = (s) => {
    setEditingSvc(s);
    setSvcForm({
      catId: String(s.category_id || ''),
      title: s.title || '',
      desc: s.description || '',
      duration: s.duration || '60 min',
      price: s.price || 1500,
      subcategories: s.subcategories || [],
      tags: s.tag_ids || [],
    });
    setShowAddSvc(true);
  };

  const handleSaveSvc = async () => {
    if (!svcForm.title) { toast('Enter a service title', 'error'); return; }
    try {
      if (editingSvc) {
        await updateService(editingSvc.id, svcForm);
        toast('Service updated ✓', 'success');
      } else {
        await addService(svcForm);
        toast('Service added ✓', 'success');
      }
      setShowAddSvc(false);
      setShowTagPicker(false);
      setShowSubPicker(false);
      setSvcForm(emptySvcForm);
      setEditingSvc(null);
    } catch (err) {
      toast(err.message || 'Failed to save service', 'error');
    }
  };

  const handleOpenEditPack = (p) => {
    setEditingPack(p);
    setPackType(p.type === 'group' ? 'group' : '1:1');
    if (p.type === 'group') {
      setGroupPackForm({
        title: p.title || '',
        description: p.description || '',
        session_count: p.session_count || 8,
        price: p.price || 4000,
        expiry_days: p.expiry_days || 90,
        category_id: String(p.category_id || ''),
        subcategories: p.subcategories || [],
        min_learners: p.min_learners || 3,
        max_learners: p.max_learners || 10,
        start_date: p.start_date ? String(p.start_date).split('T')[0] : '',
        days_of_week: p.days_of_week || [],
      });
    } else {
      setPackForm({
        title: p.title || '',
        description: p.description || '',
        session_count: p.session_count || 5,
        price: p.price || 2500,
        expiry_days: p.expiry_days || 90,
        category_id: String(p.category_id || ''),
        subcategories: p.subcategories || [],
      });
    }
    setShowAddPack(true);
  };

  const handleAddPack = async () => {
    if (!packForm.title || !packForm.session_count || !packForm.price) {
      toast('Fill all required fields', 'error');
      return;
    }
    try {
      if (editingPack) {
        const res = await apiService.updatePack(editingPack.id, { ...packForm, type: '1:1' });
        if (res.success) {
          updateExpertPack(res.data);
          setShowAddPack(false);
          setPackForm({ title: '', description: '', session_count: 5, price: 2500, expiry_days: 90, category_id: '', subcategories: [] });
          setEditingPack(null);
          toast('1:1 Pack updated ✓', 'success');
        } else { toast(res.message || 'Failed to update pack', 'error'); }
      } else {
        const res = await apiService.createPack({ ...packForm, type: '1:1' });
        if (res.success) {
          addExpertPack(res.data);
          setShowAddPack(false);
          setPackForm({ title: '', description: '', session_count: 5, price: 2500, expiry_days: 90, category_id: '', subcategories: [] });
          setPackType('1:1');
          toast('1:1 Session pack created ✓', 'success');
        } else { toast(res.message || 'Failed to create pack', 'error'); }
      }
    } catch (err) {
      toast(err.message || 'Failed to save pack', 'error');
    }
  };

  const handleAddGroupPack = async () => {
    if (!groupPackForm.title || !groupPackForm.session_count || !groupPackForm.price) {
      toast('Fill all required fields', 'error');
      return;
    }
    if (!groupPackForm.start_date) {
      toast('Select a start date', 'error');
      return;
    }
    if (groupPackForm.days_of_week.length === 0) {
      toast('Select at least one day of week', 'error');
      return;
    }
    if (groupPackForm.min_learners < 2 || groupPackForm.max_learners < groupPackForm.min_learners) {
      toast('Min learners must be ≥2 and ≤ Max learners', 'error');
      return;
    }
    const sessionDaysInWeek = groupPackForm.days_of_week.length;
    const weeksNeeded = Math.ceil(groupPackForm.session_count / sessionDaysInWeek);
    const calculatedExpiry = Math.max(90, weeksNeeded * 7);
    try {
      if (editingPack) {
        const res = await apiService.updatePack(editingPack.id, { ...groupPackForm, type: 'group', expiry_days: calculatedExpiry });
        if (res.success) {
          updateExpertPack(res.data);
          setShowAddPack(false);
          setGroupPackForm({ title: '', description: '', session_count: 8, price: 4000, expiry_days: 90, category_id: '', subcategories: [], min_learners: 3, max_learners: 10, start_date: '', days_of_week: [] });
          setEditingPack(null);
          toast('Group pack updated ✓', 'success');
        } else { toast(res.message || 'Failed to update pack', 'error'); }
      } else {
        const res = await apiService.createPack({ ...groupPackForm, type: 'group', expiry_days: calculatedExpiry });
        if (res.success) {
          addExpertPack(res.data);
          setShowAddPack(false);
          setGroupPackForm({ title: '', description: '', session_count: 8, price: 4000, expiry_days: 90, category_id: '', subcategories: [], min_learners: 3, max_learners: 10, start_date: '', days_of_week: [] });
          setPackType('1:1');
          toast('Group Session pack created ✓', 'success');
        } else { toast(res.message || 'Failed to create pack', 'error'); }
      }
    } catch (err) {
      toast(err.message || 'Failed to save pack', 'error');
    }
  };

  const handleDeletePack = async (packId) => {
    try {
      const res = await apiService.deletePack(packId);
      if (res.success) {
        removeExpertPack(packId);
        toast('Pack deleted ✓', 'success');
      } else {
        toast(res.message || 'Failed to delete pack', 'error');
      }
    } catch (err) {
      toast(err.message || 'Failed to delete pack', 'error');
    }
  };

  const availableSubcats = categories.find(c => c.id === svcForm.catId)?.subs || [];

  const joinSession = (booking) => {
    if (!booking.videoRoom) { toast('Video room not ready', 'error'); return; }
    navigate(`/session/${booking.id}`);
  };

  const getUserName = (booking) => booking.learner_name || 'Learner';

  const handleTabClick = (tabId) => {
    if (tabId === 'logout') {
      localStorage.removeItem('expert_tab');
      logout();
      navigate('/');
      toast('Logged out successfully', 'success');
    } else {
      localStorage.setItem('expert_tab', tabId);
      setActiveTab(tabId);
    }
  };

  const handleCompleteBooking = async (bookingId) => {
    try {
      const res = await apiService.completeBooking(bookingId);
      if (res.success) {
        toast('Session marked completed! Earnings credited to wallet ✓', 'success');
        // Refresh wallet to reflect new balance
        await loadWallet();
      } else {
        toast(res.message || 'Failed to complete booking', 'error');
      }
    } catch (err) {
      toast(err.message || 'Failed to complete booking', 'error');
    }
  };

  const handlePayoutRequest = async () => {
    if (!payoutForm.amount || Number(payoutForm.amount) <= 0) {
      toast('Enter a valid amount', 'error');
      return;
    }
    if (Number(payoutForm.amount) < 500) {
      toast('Minimum payout is ₹500', 'error');
      return;
    }
    if (payoutMethod === 'upi' && !payoutForm.upi_id) {
      toast('Enter UPI ID', 'error');
      return;
    }
    if (payoutMethod === 'bank' && (!payoutForm.bank_name || !payoutForm.account_number || !payoutForm.ifsc_code)) {
      toast('Enter complete bank details', 'error');
      return;
    }

    setPayoutLoading(true);
    try {
      const payload = payoutMethod === 'upi'
        ? { amount: Number(payoutForm.amount), upi_id: payoutForm.upi_id }
        : { amount: Number(payoutForm.amount), bank_name: payoutForm.bank_name, account_number: payoutForm.account_number, ifsc_code: payoutForm.ifsc_code };

      const res = await apiService.requestPayout(payload);
      if (res.success) {
        toast('Payout request submitted ✓', 'success');
        setPayoutForm({ amount: '', upi_id: '', bank_name: '', account_number: '', ifsc_code: '' });
        // Refresh wallet and payouts
        await loadWallet();
        const payoutsRes = await apiService.getPayouts();
        if (payoutsRes?.success) setPayouts(payoutsRes.data);
      } else {
        toast(res.message || 'Failed to submit payout request', 'error');
      }
    } catch (err) {
      toast(err.message || 'Failed to submit payout request', 'error');
    } finally {
      setPayoutLoading(false);
    }
  };

  return (
    <div className="dashboard-layout" style={{ display: 'flex', minHeight: 'calc(100vh - 60px)' }}>
      {/* Sidebar */}
      <div className="sidebar">
        {TABS.map(({ id, label, Icon }) => (
          <div
            key={id}
            className={`sidebar-item ${activeTab === id ? 'active' : ''} ${id === 'logout' ? 'logout-item' : ''}`}
            onClick={() => handleTabClick(id)}
            style={id === 'logout' ? { color: '#C13030' } : {}}
          >
            <Icon size={16} />
            <span className="sidebar-label">{label}</span>
            {id === 'bookings' && pending.length > 0 && (
              <span className="sidebar-badge">{pending.length}</span>
            )}
          </div>
        ))}
      </div>

      {/* Main */}
      <div className="dashboard-content" style={{ flex: 1, padding: 28, overflow: 'auto' }}>

        {/* ── Overview ── */}
        {activeTab === 'overview' && (
          <div className="animate-fadeUp">
            <SectionHeader title={`Good ${new Date().getHours() < 12 ? 'morning' : 'evening'}, ${user?.name?.split(' ')[0]} 👋`} subtitle="Here's your teaching dashboard at a glance." />
            <div className="grid-4 mb-24">
              <StatCard
                label="This Month Earned"
                value={walletLoading ? '…' : `₹${(wallet?.thisMonthEarned || 0).toLocaleString('en-IN')}`}
                delta={`${completed.length} session${completed.length !== 1 ? 's' : ''} completed`}
                accent="var(--teal)"
              />
              <StatCard
                label="Wallet Balance"
                value={walletLoading ? '…' : `₹${(wallet?.balance || 0).toLocaleString('en-IN')}`}
                delta="Available for payout"
              />
              <StatCard
                label="Pending Requests"
                value={String(pending.length)}
                delta={pending.length > 0 ? 'Needs your response' : 'All caught up!'}
                accent={pending.length > 0 ? 'var(--saffron)' : undefined}
              />
              <StatCard
                label="Total Bookings"
                value={String(myBookings.length)}
                delta={`${confirmed.length} confirmed · ${completed.length} completed`}
              />
            </div>

            <h3 style={{ fontWeight: 700, marginBottom: 12, fontSize: 16 }}>Pending booking requests</h3>
            {pending.length === 0
              ? <EmptyState icon="🎉" title="All caught up!" description="No pending requests right now." />
              : pending.map(b => (
                <div key={b.id} className="slot-row mb-8">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{getUserName(b)} → <span style={{ color: 'var(--ink3)' }}>{b.service}</span></div>
                    <div style={{ fontSize: 12, color: 'var(--ink4)', marginTop: 2 }}>{b.slot.date} · {b.slot.start}–{b.slot.end}</div>
                    {b.message && <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 4, fontStyle: 'italic' }}>"{b.message}"</div>}
                  </div>
                  <div style={{ fontWeight: 700, color: 'var(--saffron)' }}>₹{b.price?.toLocaleString()}</div>
                  <button onClick={async () => { try { await acceptBooking(b.id); toast('Booking confirmed! Video link generated ✓', 'success'); } catch { toast('Failed to confirm', 'error'); }}} className="btn btn-teal btn-sm">Accept ✓</button>
                  <button onClick={async () => { try { await declineBooking(b.id); toast('Booking declined.'); } catch { toast('Failed to decline', 'error'); }}} className="btn btn-secondary btn-sm">Decline</button>
                </div>
              ))
            }

            <h3 style={{ fontWeight: 700, marginBottom: 12, marginTop: 28, fontSize: 16 }}>Upcoming confirmed sessions</h3>
            {confirmed.length === 0
              ? <EmptyState icon="📅" title="No confirmed sessions yet." />
              : confirmed.map(b => (
                <div key={b.id} className="slot-row mb-8">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{b.service}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink4)', marginTop: 2 }}>{b.slot.date} · {b.slot.start}–{b.slot.end}</div>
                  </div>
                  <BookingBadge status="confirmed" />
                  <button onClick={() => joinSession(b)} className="btn btn-primary btn-sm" style={{ gap: 6 }}><Video size={13} /> Join</button>
                  <button onClick={() => handleCompleteBooking(b.id)} className="btn btn-teal btn-sm">Complete</button>
                </div>
              ))
            }
          </div>
        )}

        {/* ── Calendar ── */}
        {activeTab === 'calendar' && (
          <div className="animate-fadeUp">
            <SectionHeader title="Calendar & Slot Management" right={
              <button onClick={() => { const t=new Date(); setSlotForm(f=>({...f,date:`${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`,service:expertServices[0]?.title||''})); setShowAddSlot(true); }} className="btn btn-primary btn-sm"><Plus size={14}/> Add Slot</button>
            } />

            {/* Session Type Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink2)', whiteSpace: 'nowrap' }}>Filter:</span>
              {[{ value: '1:1', label: '👤 1:1 Session' }, { value: 'group', label: '🎓 Group Class' }].map(opt => (
                <button key={opt.value} onClick={() => setSlotTypeFilter(opt.value)} style={{
                  padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  border: `1.5px solid ${slotTypeFilter === opt.value ? 'var(--teal)' : 'var(--border2)'}`,
                  background: slotTypeFilter === opt.value ? '#E3F4F2' : 'transparent',
                  color: slotTypeFilter === opt.value ? '#0B6E65' : 'var(--ink3)',
                  cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap'
                }}>{opt.label}</button>
              ))}
            </div>

            <div className="grid-2" style={{ gap: 28 }}>
              <div className="card card-body">
                <MiniCalendar
                  year={calDate.getFullYear()} month={calDate.getMonth()}
                  onNavigate={d => setCalDate(prev => { const n = new Date(prev); n.setMonth(n.getMonth()+d); return n; })}
                  onSelectDay={d => setSelectedDate(d)}
                  renderDay={d => {
                    const slots = slotsForDate(d);
                    const ds = `${calDate.getFullYear()}-${String(calDate.getMonth()+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
                    return {
                      hasSlots: slots.length > 0,
                      selected: selectedDate === d,
                      dots: <div>{slots.map(s => <span key={s.id} className={`cal-dot ${s.booked ? 'cal-dot-booked' : 'cal-dot-open'}`} />)}</div>
                    };
                  }}
                />
                <div style={{ marginTop: 12, display: 'flex', gap: 12, fontSize: 11, color: 'var(--ink4)' }}>
                  <span><span className="cal-dot cal-dot-open" style={{ display: 'inline-block', marginRight: 4 }}/>Open</span>
                  <span><span className="cal-dot cal-dot-booked" style={{ display: 'inline-block', marginRight: 4 }}/>Booked</span>
                </div>
              </div>
              <div>
                <h3 style={{ fontWeight: 600, marginBottom: 12 }}>
                  {selectedDate
                    ? `Slots for ${calDate.getFullYear()}-${String(calDate.getMonth()+1).padStart(2,'0')}-${String(selectedDate).padStart(2,'0')}`
                    : 'Select a date to see slots'}
                </h3>
                {selectedDate ? (() => {
                  const daySlots = slotsForDate(selectedDate);
                  return daySlots.length === 0
                    ? <EmptyState icon="+" title="No slots on this date" description={<button onClick={() => setShowAddSlot(true)} className="btn btn-ghost-saffron btn-sm">Add a slot</button>} />
                    : daySlots.map(s => (
                      <div key={s.id} className="slot-row mb-8">
                        <div className="slot-time">{s.start}–{s.end}</div>
                        <div style={{ flex: 1, fontSize: 13 }}>{s.service}</div>
                        <div style={{ fontWeight: 700, color: 'var(--saffron)', fontSize: 14 }}>₹{s.price.toLocaleString()}</div>
                        <BookingBadge status={s.booked ? 'confirmed' : 'completed'} />
                        {!s.booked && <button onClick={async () => { try { await deleteSlot(s.id); toast('Slot removed'); } catch { toast('Failed to remove slot', 'error'); } }} className="btn btn-secondary btn-sm"><Trash2 size={12}/></button>}
                      </div>
                    ));
                })() : null}
              </div>
            </div>
          </div>
        )}

        {/* ── Bookings ── */}
        {activeTab === 'bookings' && (
          <div className="animate-fadeUp">
            <SectionHeader title="All Bookings" />
            <div className="tabs-row">
              {['all','pending','confirmed','completed','declined'].map(f => (
                <div key={f} className={`tab-item ${bookingFilter === f ? 'active' : ''}`} onClick={() => setBookingFilter(f)}>{f.charAt(0).toUpperCase()+f.slice(1)}</div>
              ))}
            </div>
            <div className="card" style={{ overflow: 'hidden' }}>
              <table className="data-table">
                <thead><tr><th>Learner</th><th>Service</th><th>Date / Time</th><th>Amount</th><th>Status</th><th>Action</th></tr></thead>
                <tbody>
                  {filteredBookings.length === 0
                    ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--ink4)' }}>No bookings found</td></tr>
                    : filteredBookings.map(b => (
                      <tr key={b.id}>
                        <td style={{ fontWeight: 600 }}>{getUserName(b)}</td>
                        <td>{b.service}</td>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{b.slot.date} · {b.slot.start}</td>
                        <td style={{ fontWeight: 700, color: 'var(--saffron)' }}>₹{b.price?.toLocaleString()}</td>
                        <td><BookingBadge status={b.status} /></td>
                        <td>
                          {b.status === 'pending' && (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={async () => { try { await acceptBooking(b.id); toast('Confirmed! Video link generated ✓', 'success'); } catch { toast('Failed to confirm', 'error'); }}} className="btn btn-teal btn-sm">Accept</button>
                              <button onClick={async () => { try { await declineBooking(b.id); toast('Booking declined.'); } catch { toast('Failed to decline', 'error'); }}} className="btn btn-secondary btn-sm">Decline</button>
                            </div>
                          )}
                          {b.status === 'confirmed' && <button onClick={() => joinSession(b)} className="btn btn-primary btn-sm" style={{ gap: 4 }}><Video size={12}/>Join</button>}
                          {!['pending','confirmed'].includes(b.status) && '—'}
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Profile ── */}
        {activeTab === 'profile' && (
          <ExpertProfileEdit />
        )}

        {/* ── Services & Pricing ── */}
        {activeTab === 'services' && (
          <div className="animate-fadeUp">
            <h2 style={{ fontWeight: 700, fontSize: 22, marginBottom: 4 }}>📚 Services</h2>
            <p style={{ color: 'var(--ink3)', fontSize: 14, marginBottom: 24 }}> </p>

            {/* Services Section */}
            <div style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h3 style={{ fontWeight: 700, fontSize: 18 }}>💼  Create Individaul Services</h3>
                <button onClick={() => setShowAddSvc(true)} className="btn btn-primary btn-sm"><Plus size={14}/> Add Service</button>
              </div>
              <div className="card card-body">
                {expertServices.length === 0
                  ? <EmptyState icon="💼" title="No services added yet" description="Add your first service to start accepting bookings." action={<button onClick={() => setShowAddSvc(true)} className="btn btn-primary btn-sm">+ Add Service</button>} />
                  : <div>
                      {expertServices.map(s => (
                        <div key={s.id} style={{ border: '1px solid var(--border2)', borderRadius: 10, padding: 18, marginBottom: 14, transition: 'var(--transition)' }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--saffron)'}
                          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border2)'}
                        >
                          <div className="flex items-center justify-between mb-8">
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 16 }}>{s.title}</div>
                              <div style={{ fontSize: 12, color: 'var(--ink4)', marginTop: 2 }}>{s.catId} · {s.duration}</div>
                            </div>
                            <div className="flex items-center gap-16" style={{ alignItems: 'flex-start' }}>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--saffron)' }}>₹{s.price.toLocaleString()}</div>
                                <div style={{ fontSize: 11, color: 'var(--ink4)' }}>per session</div>
                              </div>
                              <button onClick={() => handleOpenEditSvc(s)} className="btn btn-secondary btn-sm" style={{ padding: '6px 10px' }} title="Edit service"><Pencil size={14}/></button>
                              <button onClick={async () => { if (!window.confirm(`Remove "${s.title}"? This cannot be undone.`)) return; try { await removeService(s.id); toast('Service removed', 'success'); } catch { toast('Failed to remove service', 'error'); } }} className="btn btn-secondary btn-sm" style={{ padding: '6px 10px' }} title="Remove service"><Trash2 size={14}/></button>
                            </div>
                          </div>
                          {s.desc && (
                            <div style={{ fontSize: 14, color: 'var(--ink2)', lineHeight: 1.6, paddingTop: 12, borderTop: '1px solid var(--border2)' }}>
                              {s.desc}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                }
              </div>
            </div>

            {/* Session Packs Section */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h3 style={{ fontWeight: 700, fontSize: 18 }}>📦 Create Session Packs</h3>
                <button onClick={() => setShowAddPack(true)} className="btn btn-primary btn-sm"><Plus size={14}/> Create Pack</button>
              </div>
              <div className="card card-body">
                {expertPacks.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: 'var(--ink3)', lineHeight: 1.6 }}>
                    <p style={{ marginBottom: 12 }}><strong>Session packs are now displayed beautifully on your profile!</strong></p>
                    <p style={{ fontSize: 13 }}>Learners can browse, compare, and purchase your 1:1 and group session packages directly from your expert profile. Click the button to create new packs.</p>
                  </div>
                ) : (
                  <div>
                    {/* 1:1 Packs */}
                    {expertPacks.filter(p => p.type !== 'group').length > 0 && (
                      <div style={{ marginBottom: 24 }}>
                        <h4 style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: 'var(--ink2)' }}>👤 1:1 Session Packs</h4>
                        {expertPacks.filter(p => p.type !== 'group').map(p => (
                          <div key={p.id} style={{ border: '1px solid var(--border2)', borderRadius: 10, padding: 16, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>{p.title}</div>
                              <div style={{ fontSize: 12, color: 'var(--ink4)', marginTop: 4 }}>{p.session_count} sessions · {p.expiry_days} days validity</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--saffron)' }}>₹{Number(p.price).toLocaleString()}</div>
                              <button onClick={() => handleOpenEditPack(p)} className="btn btn-secondary btn-sm" title="Edit pack"><Pencil size={14}/></button>
                              <button onClick={async () => { if (!window.confirm(`Remove "${p.title}"? This cannot be undone.`)) return; try { await handleDeletePack(p.id); } catch { toast('Failed to remove pack', 'error'); } }} className="btn btn-secondary btn-sm" title="Remove pack"><Trash2 size={14}/></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Group Packs */}
                    {expertPacks.filter(p => p.type === 'group').length > 0 && (
                      <div>
                        <h4 style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: 'var(--ink2)' }}>🎓 Group Batch Packs</h4>
                        {expertPacks.filter(p => p.type === 'group').map(p => (
                          <div key={p.id} style={{ border: '1px solid var(--border2)', borderRadius: 10, padding: 16, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>{p.title}</div>
                              <div style={{ fontSize: 12, color: 'var(--ink4)', marginTop: 4 }}>
                                {p.session_count} sessions · {p.min_learners}–{p.max_learners} learners · Starts {new Date(p.start_date).toLocaleDateString('en-IN')}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--saffron)' }}>₹{Number(p.price).toLocaleString()}</div>
                              <button onClick={() => handleOpenEditPack(p)} className="btn btn-secondary btn-sm" title="Edit pack"><Pencil size={14}/></button>
                              <button onClick={async () => { if (!window.confirm(`Remove "${p.title}"? This cannot be undone.`)) return; try { await handleDeletePack(p.id); } catch { toast('Failed to remove pack', 'error'); } }} className="btn btn-secondary btn-sm" title="Remove pack"><Trash2 size={14}/></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ── Learner Requirements ── */}
            <div style={{ marginTop: 40 }}>
              <div style={{ marginBottom: 16 }}>
                <h3 style={{ fontWeight: 700, fontSize: 18 }}>🔎 Learner Requirements</h3>
                <p style={{ fontSize: 13, color: 'var(--ink3)', marginTop: 4 }}>Learners who couldn't find the right expert have posted their needs. Reach out if you can help.</p>
              </div>
              {reqBrowseLoading ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink3)' }}>Loading requirements…</div>
              ) : learnerRequirements.length === 0 ? (
                <div className="card card-body" style={{ textAlign: 'center', color: 'var(--ink3)', padding: 32 }}>
                  No open learner requirements right now. Check back later.
                </div>
              ) : (
                <div>
                  {learnerRequirements.map(r => (
                    <div key={r.id} style={{ border: '1px solid var(--border2)', borderRadius: 12, padding: 18, marginBottom: 12, background: 'var(--surface)', transition: 'var(--transition)' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--teal)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border2)'}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{r.title}</div>
                          {r.description && <p style={{ fontSize: 13, color: 'var(--ink3)', marginBottom: 8, lineHeight: 1.5 }}>{r.description}</p>}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 12, color: 'var(--ink4)' }}>
                            {r.category_name && <span>📂 {r.category_icon} {r.category_name}</span>}
                            {(r.budget_min > 0 || r.budget_max > 0) && (
                              <span>💰 ₹{r.budget_min > 0 ? Number(r.budget_min).toLocaleString() : '0'} – ₹{r.budget_max > 0 ? Number(r.budget_max).toLocaleString() : 'any'}/session</span>
                            )}
                            {r.preferred_timing && <span>🕐 {r.preferred_timing}</span>}
                            <span>👤 {r.learner_name}</span>
                            <span>📅 {new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                          </div>
                        </div>
                        <a href={`mailto:${r.learner_email || ''}?subject=Re: ${encodeURIComponent(r.title)}&body=Hi ${r.learner_name},%0A%0AI saw your requirement on MakeMyExpert and I can help!`}
                          className="btn btn-teal btn-sm" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                          onClick={e => { if (!r.learner_email) { e.preventDefault(); toast('Learner contact not available yet', 'error'); } }}>
                          ✉ Contact Learner
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        )}

        {/* ── Earnings ── */}
        {activeTab === 'earnings' && (
          <div className="animate-fadeUp">
            <SectionHeader title="Earnings Overview" />
            {walletLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink3)' }}>Loading wallet data…</div>
            ) : (
              <>
                <div className="grid-4 mb-24">
                  <StatCard label="Current Balance" value={`₹${(wallet?.balance || 0).toLocaleString()}`} delta="Available for payout" />
                  <StatCard label="This Month Earned" value={`₹${(wallet?.thisMonthEarned || 0).toLocaleString()}`} delta={`${completed.length} sessions`} accent="var(--saffron)" />
                  <StatCard label="Total Earned" value={`₹${(wallet?.total_earned || 0).toLocaleString()}`} delta="Since joining" />
                  <StatCard label="Total Withdrawn" value={`₹${(wallet?.total_withdrawn || 0).toLocaleString()}`} delta="Payouts processed" accent="var(--teal)" />
                </div>

                {/* Payout Request Form */}
                <div className="card card-body mb-24">
                  <h3 style={{ fontWeight: 700, marginBottom: 16, fontSize: 16 }}>Request Payout</h3>
                  <div className="grid-2 mb-16">
                    <button type="button" onClick={() => setPayoutMethod('upi')} style={{ padding: '10px 0', borderRadius: 8, border: payoutMethod === 'upi' ? '2px solid var(--saffron)' : '1.5px solid var(--border)', background: payoutMethod === 'upi' ? 'var(--saffron-lt)' : 'var(--surface)', color: payoutMethod === 'upi' ? 'var(--saffron)' : 'var(--ink3)', fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'var(--transition)' }}>🔗 UPI</button>
                    <button type="button" onClick={() => setPayoutMethod('bank')} style={{ padding: '10px 0', borderRadius: 8, border: payoutMethod === 'bank' ? '2px solid var(--saffron)' : '1.5px solid var(--border)', background: payoutMethod === 'bank' ? 'var(--saffron-lt)' : 'var(--surface)', color: payoutMethod === 'bank' ? 'var(--saffron)' : 'var(--ink3)', fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'var(--transition)' }}>🏦 Bank Transfer</button>
                  </div>

                  {payoutMethod === 'upi' ? (
                    <>
                      <div className="grid-2">
                        <div className="form-group">
                          <label className="form-label">Amount (₹)</label>
                          <input className="form-input" type="number" min="500" value={payoutForm.amount} onChange={e => setPayoutForm(f => ({ ...f, amount: e.target.value }))} placeholder="Minimum ₹500" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">UPI ID</label>
                          <input className="form-input" type="text" value={payoutForm.upi_id} onChange={e => setPayoutForm(f => ({ ...f, upi_id: e.target.value }))} placeholder="yourname@upi" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="grid-2">
                        <div className="form-group">
                          <label className="form-label">Amount (₹)</label>
                          <input className="form-input" type="number" min="500" value={payoutForm.amount} onChange={e => setPayoutForm(f => ({ ...f, amount: e.target.value }))} placeholder="Minimum ₹500" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Bank Name</label>
                          <input className="form-input" type="text" value={payoutForm.bank_name} onChange={e => setPayoutForm(f => ({ ...f, bank_name: e.target.value }))} placeholder="e.g. HDFC Bank" />
                        </div>
                      </div>
                      <div className="grid-2">
                        <div className="form-group">
                          <label className="form-label">Account Number</label>
                          <input className="form-input" type="text" value={payoutForm.account_number} onChange={e => setPayoutForm(f => ({ ...f, account_number: e.target.value }))} placeholder="Your account number" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">IFSC Code</label>
                          <input className="form-input" type="text" value={payoutForm.ifsc_code} onChange={e => setPayoutForm(f => ({ ...f, ifsc_code: e.target.value }))} placeholder="e.g. HDFC0001234" />
                        </div>
                      </div>
                    </>
                  )}
                  <button onClick={handlePayoutRequest} disabled={payoutLoading} className="btn btn-primary w-full" style={{ justifyContent: 'center', padding: 12 }}>
                    {payoutLoading ? 'Submitting…' : 'Request Payout'}
                  </button>
                </div>

                {/* Transactions Ledger */}
                {wallet?.transactions && wallet.transactions.length > 0 && (
                  <div className="card mb-24" style={{ overflow: 'hidden' }}>
                    <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border2)', fontWeight: 700 }}>Recent Transactions</div>
                    <table className="data-table">
                      <thead><tr><th>Type</th><th>Amount</th><th>Description</th><th>Date</th><th>Status</th></tr></thead>
                      <tbody>
                        {wallet.transactions.map((tx, i) => (
                          <tr key={i}>
                            <td style={{ fontSize: 12, fontWeight: 600 }}>{tx.type === 'session_credit' ? '✓ Earning' : '↓ Payout'}</td>
                            <td style={{ fontWeight: 700, color: tx.type === 'session_credit' ? 'var(--teal)' : 'var(--ink3)' }}>₹{Number(tx.amount).toLocaleString()}</td>
                            <td style={{ fontSize: 12, color: 'var(--ink3)' }}>{tx.description}</td>
                            <td style={{ fontSize: 11, color: 'var(--ink4)' }}>{new Date(tx.created_at).toLocaleDateString()}</td>
                            <td><BookingBadge status={tx.status === 'completed' ? 'completed' : 'pending'} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Payout History */}
                <div className="card" style={{ overflow: 'hidden' }}>
                  <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border2)', fontWeight: 700 }}>Payout History</div>
                  {payouts.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink4)', fontSize: 13 }}>No payouts yet</div>
                  ) : (
                    <table className="data-table">
                      <thead><tr><th>Amount</th><th>Method</th><th>Requested</th><th>Status</th></tr></thead>
                      <tbody>
                        {payouts.map((p, i) => (
                          <tr key={i}>
                            <td style={{ fontWeight: 700, color: 'var(--saffron)' }}>₹{Number(p.amount).toLocaleString()}</td>
                            <td style={{ fontSize: 12 }}>{p.upi_id ? `UPI: ${p.upi_id}` : `Bank: ${p.bank_name}`}</td>
                            <td style={{ fontSize: 11, color: 'var(--ink4)' }}>{new Date(p.requested_at).toLocaleDateString()}</td>
                            <td><BookingBadge status={p.status === 'completed' ? 'completed' : p.status === 'processing' ? 'confirmed' : 'pending'} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Add Slot Modal */}
      {/* Bulk Slot Scheduler Modal */}
      <Modal open={showAddSlot} onClose={() => setShowAddSlot(false)} title="📅 Bulk Slot Scheduler" subtitle="Create multiple slots with recurring patterns (like Outlook)">

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          {/* ── LEFT: Input Form ── */}
          <div>
            {/* Session Type */}
            <div className="form-group">
              <label className="form-label">Session Type</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {[{ val: '1:1', label: '👤 1:1' }, { val: 'group', label: '🎓 Group' }].map(({ val, label }) => (
                  <button
                    key={val} type="button"
                    onClick={() => setBulkSlotForm(f => ({ ...f, type: val }))}
                    style={{
                      flex: 1, padding: '8px 0', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      border: bulkSlotForm.type === val ? '2px solid var(--saffron)' : '1.5px solid var(--border)',
                      background: bulkSlotForm.type === val ? 'var(--saffron-lt)' : 'var(--surface)',
                      color: bulkSlotForm.type === val ? 'var(--saffron)' : 'var(--ink3)',
                    }}
                  >{label}</button>
                ))}
              </div>
            </div>

            {/* Group specific */}
            {bulkSlotForm.type === 'group' && (
              <div className="grid-2" style={{ marginBottom: 12 }}>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: 12 }}>Min Joinees</label>
                  <input className="form-input" type="number" min={2} value={bulkSlotForm.minJoinees} onChange={e => setBulkSlotForm(f => ({ ...f, minJoinees: e.target.value }))} onBlur={() => setBulkSlotForm(f => ({ ...f, minJoinees: Math.max(2, Number(f.minJoinees) || 2) }))} style={{ fontSize: 13 }} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: 12 }}>Max Joinees</label>
                  <input className="form-input" type="number" min={bulkSlotForm.minJoinees} value={bulkSlotForm.maxJoinees} onChange={e => setBulkSlotForm(f => ({ ...f, maxJoinees: e.target.value }))} onBlur={() => setBulkSlotForm(f => ({ ...f, maxJoinees: Math.max(Number(f.minJoinees) || 2, Number(f.maxJoinees) || 2) }))} style={{ fontSize: 13 }} />
                </div>
              </div>
            )}

            {/* Start Date */}
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input className="form-input" type="date" value={bulkSlotForm.startDate} onChange={e => setBulkSlotForm(f => ({ ...f, startDate: e.target.value }))} />
            </div>

            {/* Time Slots */}
            <div className="form-group">
              <label className="form-label">Time Slots</label>
              {bulkSlotForm.timeSlots.map((slot, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <input className="form-input" type="time" value={slot.start} onChange={e => {
                    const newSlots = [...bulkSlotForm.timeSlots];
                    newSlots[i].start = e.target.value;
                    setBulkSlotForm(f => ({ ...f, timeSlots: newSlots }));
                  }} style={{ flex: 1 }} />
                  <span style={{ fontSize: 12, color: 'var(--ink3)' }}>–</span>
                  <input className="form-input" type="time" value={slot.end} onChange={e => {
                    const newSlots = [...bulkSlotForm.timeSlots];
                    newSlots[i].end = e.target.value;
                    setBulkSlotForm(f => ({ ...f, timeSlots: newSlots }));
                  }} style={{ flex: 1 }} />
                  {bulkSlotForm.timeSlots.length > 1 && (
                    <button type="button" onClick={() => setBulkSlotForm(f => ({ ...f, timeSlots: f.timeSlots.filter((_, idx) => idx !== i) }))} style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ddd', cursor: 'pointer', background: '#f5f5f5', fontSize: 12 }}>✕</button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => setBulkSlotForm(f => ({ ...f, timeSlots: [...f.timeSlots, { start: '10:00', end: '11:00' }] }))} className="btn btn-secondary btn-sm" style={{ marginTop: 8 }}>+ Add Time Slot</button>
            </div>

            {/* Recurrence */}
            <div className="form-group">
              <label className="form-label">Recurrence</label>
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {[{ val: 'none', label: 'No Repeat' }, { val: 'weekly', label: 'Weekly' }, { val: 'daily', label: 'Daily' }].map(({ val, label }) => (
                  <button key={val} type="button" onClick={() => setBulkSlotForm(f => ({ ...f, recurrence: val, recurrenceDays: [] }))} style={{
                    flex: 1, padding: '6px 0', fontSize: 11, fontWeight: 700, borderRadius: 4, cursor: 'pointer',
                    border: bulkSlotForm.recurrence === val ? '2px solid var(--teal)' : '1px solid var(--border2)',
                    background: bulkSlotForm.recurrence === val ? '#E3F4F2' : 'var(--surface)',
                    color: bulkSlotForm.recurrence === val ? 'var(--teal)' : 'var(--ink3)',
                  }}>{label}</button>
                ))}
              </div>
            </div>

            {/* Weekly Days */}
            {bulkSlotForm.recurrence === 'weekly' && (
              <div className="form-group">
                <label className="form-label" style={{ fontSize: 12 }}>Days of Week</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                  {daysOfWeekOptions.map(day => {
                    const selected = bulkSlotForm.recurrenceDays.includes(day);
                    return (
                      <button key={day} type="button" onClick={() => setBulkSlotForm(f => ({
                        ...f,
                        recurrenceDays: selected ? f.recurrenceDays.filter(d => d !== day) : [...f.recurrenceDays, day],
                      }))} style={{
                        padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        border: selected ? '2px solid #0B6E65' : '1px solid var(--border2)',
                        background: selected ? '#E3F4F2' : 'var(--surface)',
                        color: selected ? '#0B6E65' : 'var(--ink3)',
                      }}>{selected ? '✓' : ''} {day.slice(0,3)}</button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* End Date (for recurring) */}
            {bulkSlotForm.recurrence !== 'none' && (
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input className="form-input" type="date" value={bulkSlotForm.endDate} onChange={e => setBulkSlotForm(f => ({ ...f, endDate: e.target.value }))} />
              </div>
            )}

            {/* Service & Price */}
            <div className="form-group">
              <label className="form-label">Service</label>
              <select className="form-select" value={bulkSlotForm.service} onChange={e => {
                const selectedService = expertServices.find(s => s.title === e.target.value);
                setBulkSlotForm(f => ({
                  ...f,
                  service: e.target.value,
                  price: selectedService ? selectedService.price : f.price
                }));
              }}>
                <option value="">Select service…</option>
                {expertServices.map(s => <option key={s.id} value={s.title}>{s.title} (₹{s.price})</option>)}
              </select>
            </div>

            <div className="grid-2" style={{ marginBottom: 12 }}>
              <div className="form-group">
                <label className="form-label">Price (₹)</label>
                <input className="form-input" type="number" value={bulkSlotForm.price} onChange={e => setBulkSlotForm(f => ({ ...f, price: Number(e.target.value) }))} />
                <div style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 4 }}>Can be adjusted manually</div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Notes (optional)</label>
              <input className="form-input" value={bulkSlotForm.notes} placeholder="e.g. Focus on writing" onChange={e => setBulkSlotForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>

          {/* ── RIGHT: Preview ── */}
          <div style={{ borderLeft: '1px solid var(--border2)', paddingLeft: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: 'var(--ink2)' }}>
              📋 Preview ({previewSlots.length} slots)
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {previewSlots.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--ink4)', padding: 12, textAlign: 'center' }}>Select dates and recurrence to see preview</div>
              ) : (
                previewSlots.map((slot, i) => (
                  <div key={i} style={{ background: '#FAFBFC', border: '1px solid var(--border2)', borderRadius: 6, padding: 8, fontSize: 11 }}>
                    <div style={{ fontWeight: 700, color: 'var(--ink2)' }}>{new Date(slot.date).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                    <div style={{ color: 'var(--saffron)', fontSize: 12, fontWeight: 600 }}>{slot.time}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <button onClick={handleAddBulkSlots} disabled={previewSlots.length === 0 || slotCreationLoading} className="btn btn-primary w-full" style={{ justifyContent: 'center', opacity: previewSlots.length === 0 || slotCreationLoading ? 0.5 : 1, position: 'relative' }}>
          {slotCreationLoading ? (
            <>
              <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderRadius: '50%', borderTopColor: 'white', animation: 'spin 0.8s linear infinite', marginRight: 8 }} />
              Creating slots...
            </>
          ) : (
            `✓ Create ${previewSlots.length} Slots`
          )}
        </button>
      </Modal>

      {/* Add / Edit Service Modal */}
      <Modal open={showAddSvc} onClose={() => { setShowAddSvc(false); setSvcForm(emptySvcForm); setEditingSvc(null); }} title={editingSvc ? 'Edit Service' : 'Add Service'} subtitle={editingSvc ? 'Update your service details' : 'Add a service you offer to learners'}>
        {/* Category dropdown */}
        <div className="form-group">
          <label className="form-label">Category</label>
          <select className="form-select" value={svcForm.catId} onChange={e => setSvcForm(f => ({ ...f, catId: e.target.value, subcategories: [] }))}>
            <option value="">— Select a category —</option>
            {categories.filter(c => c.enabled).map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
          </select>
        </div>

        {/* Subcategories — popup picker */}
        {availableSubcats.length > 0 && (
          <div className="form-group">
            <label className="form-label">Subcategories</label>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:4 }}>
              <button type="button" onClick={() => setShowSubPicker(true)} className="btn btn-secondary btn-sm" style={{ display:'flex', alignItems:'center', gap:6 }}>
                📂 Select Subcategories
                {svcForm.subcategories.length > 0 && (
                  <span style={{ background:'#0B6E65', color:'white', borderRadius:10, padding:'1px 8px', fontSize:11, fontWeight:700 }}>{svcForm.subcategories.length}</span>
                )}
              </button>
              {svcForm.subcategories.length > 0 && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {svcForm.subcategories.map(s => (
                    <span key={s} style={{ background:'#E3F4F2', color:'#0B6E65', borderRadius:8, padding:'2px 10px', fontSize:12, fontWeight:600 }}>{s}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="form-group"><label className="form-label">Service Title</label><input className="form-input" placeholder="e.g. IELTS Exam Preparation" value={svcForm.title} onChange={e=>setSvcForm(f=>({...f,title:e.target.value}))}/></div>
        <div className="form-group"><label className="form-label">Description</label><textarea className="form-textarea" placeholder="Describe what learners will get…" value={svcForm.desc} onChange={e=>setSvcForm(f=>({...f,desc:e.target.value}))}/></div>
        <div className="grid-2">
          <div className="form-group"><label className="form-label">Duration</label>
            <select className="form-select" value={svcForm.duration} onChange={e=>setSvcForm(f=>({...f,duration:e.target.value}))}>
              {['30 min','45 min','60 min','90 min'].map(d=><option key={d}>{d}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">Price (₹)</label><input className="form-input" type="number" value={svcForm.price} onChange={e=>setSvcForm(f=>({...f,price:Number(e.target.value)}))}/></div>
        </div>

        {/* Tags — opens popup */}
        <div className="form-group">
          <label className="form-label">Tags</label>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:4 }}>
            <button type="button" onClick={() => setShowTagPicker(true)} className="btn btn-secondary btn-sm" style={{ display:'flex', alignItems:'center', gap:6 }}>
              🏷️ Select Tags
              {svcForm.tags.length > 0 && (
                <span style={{ background:'#0B6E65', color:'white', borderRadius:10, padding:'1px 8px', fontSize:11, fontWeight:700 }}>{svcForm.tags.length}</span>
              )}
            </button>
            {svcForm.tags.length > 0 && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {svcForm.tags.map(tid => {
                  const t = tags.find(x => x.id === tid);
                  return t ? <span key={tid} style={{ background:'#E3F4F2', color:'#0B6E65', borderRadius:20, padding:'2px 10px', fontSize:12, fontWeight:600 }}>{t.name}</span> : null;
                })}
              </div>
            )}
          </div>
        </div>

        <button onClick={handleSaveSvc} className="btn btn-primary w-full" style={{justifyContent:'center'}}>{editingSvc ? 'Update Service' : 'Add Service'}</button>
      </Modal>

      {/* Subcategory Picker Popup */}
      <Modal open={showSubPicker} onClose={() => setShowSubPicker(false)} title="Select Subcategories" subtitle="Choose all that apply">
        <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:20 }}>
          {availableSubcats.map(sub => {
            const selected = svcForm.subcategories.includes(sub);
            return (
              <button
                key={sub} type="button"
                onClick={() => setSvcForm(f => ({
                  ...f,
                  subcategories: selected ? f.subcategories.filter(s => s !== sub) : [...f.subcategories, sub],
                }))}
                style={{
                  padding:'6px 16px', borderRadius:8, fontSize:13, cursor:'pointer', border:'1.5px solid',
                  borderColor: selected ? '#0B6E65' : 'var(--border2)',
                  background: selected ? '#E3F4F2' : 'var(--bg)',
                  color: selected ? '#0B6E65' : 'var(--ink3)',
                  fontWeight: selected ? 700 : 400, transition:'all 0.15s',
                }}
              >{selected ? '✓ ' : ''}{sub}</button>
            );
          })}
        </div>
        <button onClick={() => setShowSubPicker(false)} className="btn btn-primary w-full" style={{ justifyContent:'center' }}>
          Done{svcForm.subcategories.length > 0 ? ` (${svcForm.subcategories.length} selected)` : ''}
        </button>
      </Modal>

      {/* Tag Picker Popup */}
      <Modal open={showTagPicker} onClose={() => setShowTagPicker(false)} title="Select Tags" subtitle="Choose tags that describe this service">
        <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:20 }}>
          {tags.map(tag => {
            const selected = svcForm.tags.includes(tag.id);
            return (
              <button
                key={tag.id} type="button"
                onClick={() => setSvcForm(f => ({
                  ...f,
                  tags: selected ? f.tags.filter(t => t !== tag.id) : [...f.tags, tag.id],
                }))}
                style={{
                  padding:'6px 16px', borderRadius:20, fontSize:13, cursor:'pointer', border:'1.5px solid',
                  borderColor: selected ? '#0B6E65' : 'var(--border2)',
                  background: selected ? '#E3F4F2' : 'var(--bg)',
                  color: selected ? '#0B6E65' : 'var(--ink3)',
                  fontWeight: selected ? 700 : 400,
                  transition:'all 0.15s',
                }}
              >{selected ? '✓ ' : ''}{tag.name}</button>
            );
          })}
        </div>
        <button onClick={() => setShowTagPicker(false)} className="btn btn-primary w-full" style={{ justifyContent:'center' }}>
          Done{svcForm.tags.length > 0 ? ` (${svcForm.tags.length} selected)` : ''}
        </button>
      </Modal>

      {/* Add / Edit Pack Modal */}
      <Modal open={showAddPack} onClose={() => { setShowAddPack(false); setShowSubPickerPack(false); setShowSubPickerGroupPack(false); setEditingPack(null); }} title={editingPack ? `Edit ${packType === 'group' ? 'Group' : '1:1'} Pack` : (packType === '1:1' ? 'Create 1:1 Session Pack' : 'Create Group Session Pack')} subtitle={editingPack ? 'Update your pack details' : (packType === '1:1' ? 'Offer bulk 1:1 session packages at a discounted rate' : 'Offer group batch packages with scheduled sessions')}>
        {/* Pack Type Toggle — locked when editing */}
        <div className="form-group">
          <label className="form-label">Package Type</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {[{ val: '1:1', label: '👤 1:1 Sessions' }, { val: 'group', label: '🎓 Group Batch' }].map(({ val, label }) => (
              <button
                key={val}
                type="button"
                onClick={() => !editingPack && setPackType(val)}
                disabled={!!editingPack}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  borderRadius: 8,
                  border: packType === val ? '2px solid var(--saffron)' : '1.5px solid var(--border)',
                  background: packType === val ? 'var(--saffron-lt)' : 'var(--surface)',
                  color: packType === val ? 'var(--saffron)' : 'var(--ink3)',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: editingPack ? 'default' : 'pointer',
                  opacity: editingPack && packType !== val ? 0.4 : 1,
                  transition: 'var(--transition)',
                }}
              >{label}</button>
            ))}
          </div>
        </div>

        {/* ── 1:1 Pack Form ── */}
        {packType === '1:1' && (
          <>
            {/* Category dropdown */}
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-select" value={packForm.category_id} onChange={e => setPackForm(f => ({ ...f, category_id: e.target.value, subcategories: [] }))}>
                <option value="">— Select a category —</option>
                {categories.filter(c => c.enabled).map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
            </div>

            {/* Subcategories — popup picker */}
            {availableSubcatsPack.length > 0 && (
              <div className="form-group">
                <label className="form-label">Subcategories (optional)</label>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:4 }}>
                  <button type="button" onClick={() => setShowSubPickerPack(true)} className="btn btn-secondary btn-sm" style={{ display:'flex', alignItems:'center', gap:6 }}>
                    📂 Select Subcategories
                    {packForm.subcategories.length > 0 && (
                      <span style={{ background:'#0B6E65', color:'white', borderRadius:10, padding:'1px 8px', fontSize:11, fontWeight:700 }}>{packForm.subcategories.length}</span>
                    )}
                  </button>
                  {packForm.subcategories.length > 0 && (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                      {packForm.subcategories.map(s => (
                        <span key={s} style={{ background:'#E3F4F2', color:'#0B6E65', borderRadius:8, padding:'2px 10px', fontSize:12, fontWeight:600 }}>{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Pack Title</label>
              <input className="form-input" placeholder="e.g. 5-Session Intensive Bundle" value={packForm.title} onChange={e => setPackForm(f => ({ ...f, title: e.target.value }))} />
            </div>

            <div className="form-group">
              <label className="form-label">Description (optional)</label>
              <textarea className="form-textarea" placeholder="What's included in this pack? Any special benefits?" value={packForm.description} onChange={e => setPackForm(f => ({ ...f, description: e.target.value }))} />
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Number of Sessions</label>
                <input className="form-input" type="number" min="1" value={packForm.session_count} onChange={e => setPackForm(f => ({ ...f, session_count: e.target.value }))} onBlur={() => setPackForm(f => ({ ...f, session_count: Math.max(1, Number(f.session_count) || 1) }))} />
              </div>

              <div className="form-group">
                <label className="form-label">Pack Price (₹)</label>
                <input className="form-input" type="number" min="0" value={packForm.price} onChange={e => setPackForm(f => ({ ...f, price: Math.max(0, Number(e.target.value)) }))} />
              </div>
            </div>

            {/* Price Breakdown */}
            {packForm.session_count > 0 && packForm.price > 0 && (
              <div style={{ background: '#FAFBFC', border: '1px solid var(--border2)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
                  <span style={{ color: 'var(--ink3)' }}>Price per session</span>
                  <span style={{ fontWeight: 700, color: 'var(--saffron)' }}>₹{(packForm.price / packForm.session_count).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, borderTop: '1px solid var(--border2)', paddingTop: 8 }}>
                  <span style={{ color: 'var(--ink3)' }}>Total pack price</span>
                  <span style={{ fontWeight: 700, color: 'var(--saffron)', fontSize: 16 }}>₹{packForm.price.toLocaleString('en-IN')}</span>
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Validity (days)</label>
              <input className="form-input" type="number" min="1" value={packForm.expiry_days} onChange={e => setPackForm(f => ({ ...f, expiry_days: e.target.value }))} onBlur={() => setPackForm(f => ({ ...f, expiry_days: Math.max(1, Number(f.expiry_days) || 1) }))} />
              <div style={{ fontSize: 12, color: 'var(--ink4)', marginTop: 4 }}>Sessions must be booked within this period from purchase</div>
            </div>

            <button onClick={handleAddPack} className="btn btn-primary w-full" style={{ justifyContent: 'center' }}>{editingPack ? 'Update 1:1 Pack' : 'Create 1:1 Pack'}</button>
          </>
        )}

        {/* ── Group Pack Form ── */}
        {packType === 'group' && (
          <>
            {/* Category dropdown */}
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-select" value={groupPackForm.category_id} onChange={e => setGroupPackForm(f => ({ ...f, category_id: e.target.value, subcategories: [] }))}>
                <option value="">— Select a category —</option>
                {categories.filter(c => c.enabled).map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
            </div>

            {/* Subcategories — popup picker */}
            {availableSubcatsGroupPack.length > 0 && (
              <div className="form-group">
                <label className="form-label">Subcategories (optional)</label>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:4 }}>
                  <button type="button" onClick={() => setShowSubPickerGroupPack(true)} className="btn btn-secondary btn-sm" style={{ display:'flex', alignItems:'center', gap:6 }}>
                    📂 Select Subcategories
                    {groupPackForm.subcategories.length > 0 && (
                      <span style={{ background:'#0B6E65', color:'white', borderRadius:10, padding:'1px 8px', fontSize:11, fontWeight:700 }}>{groupPackForm.subcategories.length}</span>
                    )}
                  </button>
                  {groupPackForm.subcategories.length > 0 && (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                      {groupPackForm.subcategories.map(s => (
                        <span key={s} style={{ background:'#E3F4F2', color:'#0B6E65', borderRadius:8, padding:'2px 10px', fontSize:12, fontWeight:600 }}>{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Batch Title</label>
              <input className="form-input" placeholder="e.g. Advanced Python Batch" value={groupPackForm.title} onChange={e => setGroupPackForm(f => ({ ...f, title: e.target.value }))} />
            </div>

            <div className="form-group">
              <label className="form-label">Description (optional)</label>
              <textarea className="form-textarea" placeholder="Curriculum overview, target level, prerequisites…" value={groupPackForm.description} onChange={e => setGroupPackForm(f => ({ ...f, description: e.target.value }))} />
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Min Learners</label>
                <input className="form-input" type="number" min="2" value={groupPackForm.min_learners} onChange={e => setGroupPackForm(f => ({ ...f, min_learners: e.target.value }))} onBlur={() => setGroupPackForm(f => ({ ...f, min_learners: Math.max(2, Number(f.min_learners) || 2) }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Max Learners</label>
                <input className="form-input" type="number" min={groupPackForm.min_learners} value={groupPackForm.max_learners} onChange={e => setGroupPackForm(f => ({ ...f, max_learners: e.target.value }))} onBlur={() => setGroupPackForm(f => ({ ...f, max_learners: Math.max(Number(f.min_learners) || 2, Number(f.max_learners) || 2) }))} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input className="form-input" type="date" value={groupPackForm.start_date} onChange={e => setGroupPackForm(f => ({ ...f, start_date: e.target.value }))} />
            </div>

            <div className="form-group">
              <label className="form-label">Sessions per Week</label>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:8, marginBottom:12 }}>
                {daysOfWeekOptions.map(day => {
                  const selected = groupPackForm.days_of_week.includes(day);
                  const canSelect = selected || groupPackForm.days_of_week.length < groupPackForm.session_count;

                  return (
                    <button
                      key={day} type="button"
                      onClick={() => {
                        if (canSelect) {
                          setGroupPackForm(f => ({
                            ...f,
                            days_of_week: selected ? f.days_of_week.filter(d => d !== day) : [...f.days_of_week, day],
                          }));
                        }
                      }}
                      disabled={!canSelect}
                      style={{
                        padding:'6px 12px', borderRadius:6, fontSize:12, cursor: canSelect ? 'pointer' : 'not-allowed', border:'1.5px solid',
                        borderColor: selected ? '#0B6E65' : canSelect ? 'var(--border2)' : '#ddd',
                        background: selected ? '#E3F4F2' : canSelect ? 'var(--bg)' : '#f5f5f5',
                        color: selected ? '#0B6E65' : canSelect ? 'var(--ink3)' : '#999',
                        fontWeight: selected ? 700 : 400,
                        transition:'all 0.15s',
                        opacity: canSelect ? 1 : 0.5,
                      }}
                    >{selected ? '✓ ' : ''}{day.slice(0,3)}</button>
                  );
                })}
              </div>

              {/* Show upcoming class dates */}
              {groupPackForm.start_date && groupPackForm.days_of_week.length > 0 && (() => {
                const startDate = new Date(groupPackForm.start_date);
                const upcomingDates = [];
                const dayMap = { 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4, 'Friday': 5, 'Saturday': 6, 'Sunday': 0 };

                // Get dates up to session_count
                for (let i = 0; i < 365 && upcomingDates.length < groupPackForm.session_count; i++) {
                  const d = new Date(startDate);
                  d.setDate(d.getDate() + i);
                  const dayName = d.toLocaleString('en-US', { weekday: 'long' });
                  if (groupPackForm.days_of_week.includes(dayName)) {
                    upcomingDates.push(d.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' }));
                  }
                }

                return (
                  <div style={{ background: '#E3F4F2', border: '1px solid #0B6E65', borderRadius: 8, padding: 12, marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#0B6E65', marginBottom: 8 }}>📅 Session Schedule ({upcomingDates.length} sessions)</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {upcomingDates.map((date, i) => (
                        <span key={i} style={{ background: 'white', border: '1px solid #0B6E65', borderRadius: 4, padding: '4px 8px', fontSize: 11, fontWeight: 600, color: '#0B6E65' }}>
                          {date}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Number of Sessions</label>
                <input className="form-input" type="number" min="1" value={groupPackForm.session_count} onChange={e => setGroupPackForm(f => ({ ...f, session_count: e.target.value }))} onBlur={() => setGroupPackForm(f => ({ ...f, session_count: Math.max(1, Number(f.session_count) || 1) }))} />
              </div>

              <div className="form-group">
                <label className="form-label">Batch Price (₹)</label>
                <input className="form-input" type="number" min="0" value={groupPackForm.price} onChange={e => setGroupPackForm(f => ({ ...f, price: Math.max(0, Number(e.target.value)) }))} />
              </div>
            </div>

            {/* Price Breakdown */}
            {groupPackForm.session_count > 0 && groupPackForm.price > 0 && (
              <div style={{ background: '#FAFBFC', border: '1px solid var(--border2)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
                  <span style={{ color: 'var(--ink3)' }}>Price per session</span>
                  <span style={{ fontWeight: 700, color: 'var(--saffron)' }}>₹{(groupPackForm.price / groupPackForm.session_count).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, borderTop: '1px solid var(--border2)', paddingTop: 8 }}>
                  <span style={{ color: 'var(--ink3)' }}>Total batch price</span>
                  <span style={{ fontWeight: 700, color: 'var(--saffron)', fontSize: 16 }}>₹{groupPackForm.price.toLocaleString('en-IN')}</span>
                </div>
              </div>
            )}

            <button onClick={handleAddGroupPack} className="btn btn-primary w-full" style={{ justifyContent: 'center' }}>{editingPack ? 'Update Group Pack' : 'Create Group Pack'}</button>
          </>
        )}
      </Modal>

      {/* Subcategory Picker Popup for 1:1 Packs */}
      <Modal open={showSubPickerPack} onClose={() => setShowSubPickerPack(false)} title="Select Subcategories" subtitle="Choose all that apply to this pack">
        <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:20 }}>
          {availableSubcatsPack.map(sub => {
            const selected = packForm.subcategories.includes(sub);
            return (
              <button
                key={sub} type="button"
                onClick={() => setPackForm(f => ({
                  ...f,
                  subcategories: selected ? f.subcategories.filter(s => s !== sub) : [...f.subcategories, sub],
                }))}
                style={{
                  padding:'6px 16px', borderRadius:8, fontSize:13, cursor:'pointer', border:'1.5px solid',
                  borderColor: selected ? '#0B6E65' : 'var(--border2)',
                  background: selected ? '#E3F4F2' : 'var(--bg)',
                  color: selected ? '#0B6E65' : 'var(--ink3)',
                  fontWeight: selected ? 700 : 400, transition:'all 0.15s',
                }}
              >{selected ? '✓ ' : ''}{sub}</button>
            );
          })}
        </div>
        <button onClick={() => setShowSubPickerPack(false)} className="btn btn-primary w-full" style={{ justifyContent:'center' }}>
          Done{packForm.subcategories.length > 0 ? ` (${packForm.subcategories.length} selected)` : ''}
        </button>
      </Modal>

      {/* Subcategory Picker Popup for Group Packs */}
      <Modal open={showSubPickerGroupPack} onClose={() => setShowSubPickerGroupPack(false)} title="Select Subcategories" subtitle="Choose all that apply to this batch">
        <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:20 }}>
          {availableSubcatsGroupPack.map(sub => {
            const selected = groupPackForm.subcategories.includes(sub);
            return (
              <button
                key={sub} type="button"
                onClick={() => setGroupPackForm(f => ({
                  ...f,
                  subcategories: selected ? f.subcategories.filter(s => s !== sub) : [...f.subcategories, sub],
                }))}
                style={{
                  padding:'6px 16px', borderRadius:8, fontSize:13, cursor:'pointer', border:'1.5px solid',
                  borderColor: selected ? '#0B6E65' : 'var(--border2)',
                  background: selected ? '#E3F4F2' : 'var(--bg)',
                  color: selected ? '#0B6E65' : 'var(--ink3)',
                  fontWeight: selected ? 700 : 400, transition:'all 0.15s',
                }}
              >{selected ? '✓ ' : ''}{sub}</button>
            );
          })}
        </div>
        <button onClick={() => setShowSubPickerGroupPack(false)} className="btn btn-primary w-full" style={{ justifyContent:'center' }}>
          Done{groupPackForm.subcategories.length > 0 ? ` (${groupPackForm.subcategories.length} selected)` : ''}
        </button>
      </Modal>
    </div>
  );
}
